import { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import './CheckinGuestPage.css';

// ------------------------------------------------------------
// 言語連動カレンダー（自前DatePicker）
//   ネイティブ <input type="date"> のポップアップはブラウザ/OSロケール依存で、
//   アプリで選択した言語（i18n.language）に追従しない（月名・曜日が日本語固定になる）。
//   そのため Intl.DateTimeFormat(locale) で月名・曜日名を生成する自前UIに置き換える。
//   値は従来どおり 'YYYY-MM-DD' 文字列で入出力するため、呼び出し側の変更は不要。
// ------------------------------------------------------------
function pad2(n: number) { return String(n).padStart(2, '0'); }
function toYMD(y: number, m: number, d: number) { return `${y}-${pad2(m + 1)}-${pad2(d)}`; }

function I18nDatePicker({ value, locale, onChange, placeholder }: {
  value: string; locale: string; onChange: (v: string) => void; placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const selected = value ? new Date(`${value}T00:00:00`) : null;
  const today = new Date();
  const [view, setView] = useState(() => {
    const base = selected || today;
    return { y: base.getFullYear(), m: base.getMonth() };
  });

  // 外側クリックで閉じる
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // 選択言語で月名・曜日名を生成（Intl がロケールに追従する）
  const monthLabel = new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long' })
    .format(new Date(view.y, view.m, 1));
  const weekdayFmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  const weekdays = Array.from({ length: 7 }, (_, i) => weekdayFmt.format(new Date(2021, 7, 1 + i))); // 2021-08-01 は日曜
  const displayFmt = new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' });
  const displayValue = selected ? displayFmt.format(selected) : '';

  const firstDow = new Date(view.y, view.m, 1).getDay();
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const prevMonth = () => setView((v) => v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 });
  const nextMonth = () => setView((v) => v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 });
  const isSameDay = (d: number, ref: Date) =>
    ref.getFullYear() === view.y && ref.getMonth() === view.m && ref.getDate() === d;

  return (
    <div className="cg-dp" ref={boxRef}>
      <button type="button" className="cg-input cg-input-lg cg-dp-field" onClick={() => setOpen((o) => !o)}>
        {displayValue || <span className="cg-dp-placeholder">{placeholder}</span>}
      </button>
      {open && (
        <div className="cg-dp-pop" role="dialog">
          <div className="cg-dp-head">
            <button type="button" className="cg-dp-nav" onClick={prevMonth} aria-label="prev">‹</button>
            <span className="cg-dp-month">{monthLabel}</span>
            <button type="button" className="cg-dp-nav" onClick={nextMonth} aria-label="next">›</button>
          </div>
          <div className="cg-dp-grid cg-dp-dow">
            {weekdays.map((w, i) => <span key={i} className="cg-dp-dow-cell">{w}</span>)}
          </div>
          <div className="cg-dp-grid">
            {cells.map((d, i) => d === null
              ? <span key={i} className="cg-dp-cell empty" />
              : <button key={i} type="button"
                  className={`cg-dp-cell${selected && isSameDay(d, selected) ? ' selected' : ''}${isSameDay(d, today) ? ' today' : ''}`}
                  onClick={() => { onChange(toYMD(view.y, view.m, d)); setOpen(false); }}>{d}</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------
// 当日チェックイン業務用（受付端末）画面  /checkin
//   設計: .plan/checkin_identity_verification_design.md
//   入室後、ゲストが施設タブレットで操作するセルフチェックイン受付UI。
//   ステップ: 1．予約照会（予約番号＋チェックイン日） 2．顔＋旅券の提出 3．完了（ルール案内）
//   OTA/自社を区別せず、全予約「予約番号＋チェックイン日」で照会（/ota-verify）。
//   提出元は ?source=tablet を付けて記録。ロジックは既存の passport API を再利用。
// ------------------------------------------------------------

interface GuestSlot {
  id: string; seq_no: number; display_name: string; name: string; registered: boolean; status: string;
  verification_status: string; passport_pre_registered: boolean; face_submitted: boolean; passport_submitted: boolean;
}
interface VerifyResult {
  token: string; reservation_id: string; property_name: string;
  checkin_date: string; checkout_date: string; guest_count: number; guests: GuestSlot[];
}

type Step = 'verify' | 'guests' | 'done';

// チェックインは写真撮影のみ（PDF不要）。iOS Safari は accept に PDF が混ざると capture でカメラが黒くなるため画像のみ。
const ACCEPT = 'image/*';
const LANGS = [
  { code: 'ja', label: '日本語' }, { code: 'en', label: 'English' }, { code: 'zh', label: '中文' },
];
// 主要3言語はボタン、その他の言語（仏・西・韓）は右側のドロップダウンで提供する。
const LANGS_MORE = [
  { code: 'fr', label: 'Français' }, { code: 'es', label: 'Español' }, { code: 'ko', label: '한국어' },
];

export default function CheckinGuestPage() {
  const { t, i18n } = useTranslation();
  const params = new URLSearchParams(window.location.search);
  const submitSource = (params.get('source') === 'tablet') ? 'tablet' : 'guest_device';
  // 現場iPad受付方式: ?facility=XX があれば施設コードを固定し、ゲストには入力させない。
  //   ホーム画面アプリ(standalone)では iOS が start_url を優先して追加時URLのクエリを
  //   捨てるため、URL に facility があれば localStorage に記憶し、無ければ記憶値を使う。
  //   これで /checkin?facility=FT で追加した端末は FT、?facility=SD の端末は SD を保持する。
  const FACILITY_KEY = 'checkin_facility_code';
  const sanitizeFacility = (v: string) => v.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  const urlFacility = sanitizeFacility(params.get('facility') || '');
  let storedFacility = '';
  try {
    if (urlFacility) {
      // URL に指定があれば、それを端末に記憶（上書き）
      localStorage.setItem(FACILITY_KEY, urlFacility);
      storedFacility = urlFacility;
    } else {
      // URL に無ければ記憶済みの値を復元（standalone 起動でクエリが落ちた場合）
      storedFacility = sanitizeFacility(localStorage.getItem(FACILITY_KEY) || '');
    }
  } catch {
    // プライベートモード等で localStorage 不可 → URL 値のみ使用
    storedFacility = urlFacility;
  }
  const fixedFacility = urlFacility || storedFacility;

  // 管理画面の「URL発行」から渡されるチェックイン日（?date=YYYY-MM-DD）。
  //   施設コードと同様、ゲストに日付を選ばせないための事前指定。形式が正しいときだけ採用する。
  const urlDateRaw = (params.get('date') || '').trim();
  const urlDate = /^\d{4}-\d{2}-\d{2}$/.test(urlDateRaw) ? urlDateRaw : '';

  const [step, setStep] = useState<Step>('verify');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // 施設コード: URL固定があればそれを初期値に。なければ手動入力（保険運用）。
  const [facilityCode, setFacilityCode] = useState(fixedFacility);
  // チェックイン日: URL(?date=)の指定があれば初期値に。なければゲストがカレンダーで選ぶ。
  const [checkin, setCheckin] = useState(urlDate);
  const [data, setData] = useState<VerifyResult | null>(null);
  const [token, setToken] = useState('');
  const [guests, setGuests] = useState<GuestSlot[]>([]);
  const [rowBusy, setRowBusy] = useState<string>(''); // `${guestId}:${kind}` while uploading
  // 追加ゲスト（事前登録のない同伴者）の情報入力フォーム
  const [addForm, setAddForm] = useState<null | { name: string; birthdate: string; gender: string; nationality: string; passport_number: string; address: string; prev_accommodation: string; next_destination: string }>(null);

  // 最新の宿泊者リストを取得して返す。完了判定は呼び出し側で「全員が顔＋旅券を提出済み」で行う。
  const refreshGuests = useCallback(async (tk: string): Promise<GuestSlot[]> => {
    const res = await fetch(`/api/passport/guests?token=${encodeURIComponent(tk)}`);
    const d = await res.json().catch(() => ({}));
    if (res.ok) {
      const list: GuestSlot[] = d.guests || [];
      setGuests(list);
      return list;
    }
    return [];
  }, []);

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const res = await fetch('/api/passport/facility-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ facility_code: facilityCode.trim(), checkin_date: checkin }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        const map: Record<string, string> = { invalid: 'errInvalid', cancelled: 'errCancelled', expired: 'errExpired', rate_limited: 'errRateLimited', multiple: 'errMultiple' };
        setError(t(`checkin.${map[d.reason] || 'errVerify'}`));
        return;
      }
      setToken(d.token);
      setData(d);
      setGuests(d.guests || []);
      setStep('guests');
    } finally { setBusy(false); }
  }

  // 行の撮影ボタンから、顔またはパスポートを撮って即アップロードする。
  async function uploadKind(guestId: string, kind: 'face' | 'passport', f: File) {
    setError(''); setRowBusy(`${guestId}:${kind}`);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const q = `token=${encodeURIComponent(token)}&kind=${kind}&source=${submitSource}`;
      const r = await fetch(`/api/passport/guests/${guestId}/file?${q}`, { method: 'POST', body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(t('checkin.errUpload')); return; }
      // 自動では完了画面に進めない。各ゲストは顔→旅券で個別に「チェックイン完了」になり、一覧に留まる。
      // 全員まとめての完了は、一覧下部の「全員チェックイン完了」ボタンで明示的に行う。
      await refreshGuests(token);
    } finally {
      setRowBusy('');
    }
  }

  // 「ゲストを追加」: 新しい枠を作成し、情報入力フォームを開く。
  async function openAddGuest() {
    setError(''); setBusy(true);
    try {
      const res = await fetch('/api/passport/guests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.guest) { setError(t('checkin.errAddGuest')); return; }
      await refreshGuests(token);
      // 追加直後の枠に対して情報入力フォームを開く
      setAddForm({ name: '', birthdate: '', gender: '', nationality: '', passport_number: '', address: '', prev_accommodation: '', next_destination: '' });
      (window as any).__addGuestId = d.guest.id;
    } finally { setBusy(false); }
  }

  async function saveAddGuest(e: React.FormEvent) {
    e.preventDefault();
    const gid = (window as any).__addGuestId as string | undefined;
    if (!gid || !addForm) return;
    setError(''); setBusy(true);
    try {
      const r = await fetch(`/api/passport/guests/${gid}?token=${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addForm),
      });
      if (!r.ok) { setError(t('checkin.errInfoSave')); return; }
      setAddForm(null);
      (window as any).__addGuestId = undefined;
      await refreshGuests(token);
    } finally { setBusy(false); }
  }

  const LangBar = () => (
    <div className="cg-lang">
      <div className="cg-lang-btns">
        {LANGS.map((l) => (
          <button key={l.code} type="button"
            className={`cg-lang-btn ${i18n.language === l.code ? 'active' : ''}`}
            onClick={() => i18n.changeLanguage(l.code)}>{l.label}</button>
        ))}
      </div>
      <select
        className="cg-lang-more"
        aria-label="Language"
        value={LANGS_MORE.some((l) => l.code === i18n.language) ? i18n.language : ''}
        onChange={(e) => { if (e.target.value) i18n.changeLanguage(e.target.value); }}
      >
        <option value="">🌐 Other</option>
        {LANGS_MORE.map((l) => (
          <option key={l.code} value={l.code}>{l.label}</option>
        ))}
      </select>
    </div>
  );

  // ===== ステップ進行バー =====
  const steps: { key: Step; label: string }[] = [
    { key: 'verify', label: t('checkin.stepVerify') },
    { key: 'guests', label: t('checkin.stepSubmit') },
    { key: 'done', label: t('checkin.stepDone') },
  ];
  const stepIndex = steps.findIndex((s) => s.key === step);
  const Stepper = () => (
    <div className="cg-stepper">
      {steps.map((s, i) => (
        <div key={s.key} className={`cg-step ${i === stepIndex ? 'current' : ''} ${i < stepIndex ? 'past' : ''}`}>
          <span className="cg-step-no">{i + 1}</span>
          <span className="cg-step-label">{s.label}</span>
        </div>
      ))}
    </div>
  );

  // ====== 画面1: 予約照会 ======
  if (step === 'verify') {
    return (
      <div className="cg-wrap">
        <LangBar />
        <Stepper />
        <div className="cg-card cg-card-center">
          <h1 className="cg-title">{t('checkin.title')}</h1>
          <p className="cg-sub">{fixedFacility ? t('checkin.facilityFixedLead') : t('checkin.verifyLead')}</p>
          <div className="cg-notice">
            <h2 className="cg-notice-head">{t('checkin.noticeHead')}</h2>
            <p className="cg-notice-body">{t('checkin.noticeBody')}</p>
            <p className="cg-notice-emphasis">{t('checkin.noticeEmphasis1')}</p>
            <p className="cg-notice-emphasis">{t('checkin.noticeEmphasis2')}</p>
            <p className="cg-notice-foot">{t('checkin.noticeFoot')}</p>
          </div>
          <form onSubmit={handleVerify} className="cg-form">
            {fixedFacility ? (
              /* 現場iPad: 施設コードは固定。入力させず、施設コードのみ表示する。 */
              <div className="cg-facility-fixed">
                <span className="cg-facility-fixed-label">{t('checkin.facilityCode')}</span>
                <span className="cg-facility-fixed-code">{fixedFacility}</span>
              </div>
            ) : (
              /* 手動運用（保険）: 施設コードを入力させる。 */
              <label className="cg-label">{t('checkin.facilityCode')}
                <input className="cg-input cg-input-lg" value={facilityCode} required autoCapitalize="characters"
                  placeholder={t('checkin.facilityCodePlaceholder')}
                  onChange={(e) => setFacilityCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} />
              </label>
            )}
            <label className="cg-label">{t('checkin.checkinDate')}
              <I18nDatePicker value={checkin} locale={i18n.language}
                onChange={(v) => setCheckin(v)}
                placeholder={t('checkin.checkinDatePlaceholder')} />
              {/* required 相当: 未選択なら送信ボタンを無効化（下の disabled に checkin を追加） */}
            </label>
            {error && <p className="cg-error">{error}</p>}
            <button type="submit" className="cg-btn cg-btn-primary" disabled={busy || !checkin || (!fixedFacility && !facilityCode)}>
              {busy ? t('checkin.verifying') : t('checkin.verifyBtn')}
            </button>
          </form>
          {/* プライバシー保護の趣旨＋ポリシーリンク（/passport の pp-privacy-notice と同等） */}
          <div className="cg-privacy-notice">
            <div className="cg-privacy-head">
              <span className="cg-privacy-icon" aria-hidden="true">🔒</span>
              <span className="cg-privacy-title">{t('checkin.privacyHeading')}</span>
            </div>
            <p className="cg-privacy-body">{t('checkin.privacyNotice')}</p>
            <p className="cg-privacy-link">
              {t('checkin.privacyLinkPrefix')}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer">{t('checkin.privacyLinkText')}</Link>
              {t('checkin.privacyLinkSuffix')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ====== 画面2: 宿泊者一覧（誰の本人確認をするか） ======
  if (step === 'guests' && data) {
    const doneCount = guests.filter((g) => g.registered).length;
    return (
      <div className="cg-wrap">
        <LangBar />
        <Stepper />
        <div className="cg-card">
          <h1 className="cg-title">{t('checkin.guestsTitle')}</h1>
          <p className="cg-meta">{data.property_name} ／ {data.checkin_date}</p>
          <p className="cg-progress">{t('checkin.progress', { done: doneCount, total: data.guest_count })}</p>
          <p className="cg-usage">{t('checkin.usageNote')}</p>
          <div className="cg-guest-list">
            {guests.map((g) => (
              <div key={g.id} className={`cg-guest-card ${(g.face_submitted && g.passport_submitted) ? 'done' : ''}`}>
                <div className="cg-guest-top">
                  <span className="cg-guest-name">
                    {t('checkin.guestN', { n: g.seq_no })}{g.name ? `：${g.name}` : ''}
                  </span>
                  {g.face_submitted && g.passport_submitted
                    ? <span className="cg-guest-status done">{t('checkin.guestDone')}</span>
                    : <span className={`cg-pre-badge ${g.passport_pre_registered ? 'ok' : ''}`}>
                        {g.passport_pre_registered ? t('checkin.preRegistered') : t('checkin.preNotRegistered')}
                      </span>}
                </div>
                <div className="cg-capture-row">
                  <label className={`cg-capture-btn ${g.face_submitted ? 'done' : ''}`}>
                    <input type="file" accept={ACCEPT} style={{ display: 'none' }}
                      disabled={!!rowBusy}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadKind(g.id, 'face', f); e.currentTarget.value = ''; }} />
                    <span className="cg-capture-ico">📷</span>
                    {rowBusy === `${g.id}:face` ? t('checkin.uploading') : (g.face_submitted ? t('checkin.faceDone') : t('checkin.faceBtn'))}
                  </label>
                  <label className={`cg-capture-btn ${g.passport_submitted ? 'done' : ''}`}>
                    <input type="file" accept={ACCEPT} style={{ display: 'none' }}
                      disabled={!!rowBusy}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadKind(g.id, 'passport', f); e.currentTarget.value = ''; }} />
                    <span className="cg-capture-ico">🛂</span>
                    {rowBusy === `${g.id}:passport` ? t('checkin.uploading') : (g.passport_submitted ? t('checkin.passportDone') : t('checkin.passportBtn'))}
                  </label>
                </div>
              </div>
            ))}
          </div>

          {/* 事前登録のない同伴者を追加 */}
          <button className="cg-btn cg-btn-ghost cg-add-guest" onClick={openAddGuest} disabled={busy || !!addForm}>
            ＋ {t('checkin.addGuest')}
          </button>

          {error && <p className="cg-error">{error}</p>}
          {(() => {
            // 事前登録リストのゲスト（passport_pre_registered）が全員チェックイン完了なら、全員完了ボタンを有効化。
            const pre = guests.filter((g) => g.passport_pre_registered);
            const base = pre.length > 0 ? pre : guests; // 事前登録が無い場合は全枠を基準にする
            const canFinish = base.length > 0 && base.every((g) => g.face_submitted && g.passport_submitted);
            const doneCount = guests.filter((g) => g.face_submitted && g.passport_submitted).length;
            return (
              <>
                <button className="cg-btn cg-btn-primary" disabled={!canFinish} onClick={() => setStep('done')}>
                  {t('checkin.finishAll')}
                </button>
                {!canFinish && <p className="cg-finish-hint">{t('checkin.finishHint', { done: doneCount, total: guests.length })}</p>}
              </>
            );
          })()}
        </div>

        {/* 追加ゲストの情報入力フォーム（フル登録） */}
        {addForm && (
          <div className="cg-modal-overlay" onClick={() => !busy && setAddForm(null)}>
            <form className="cg-modal" onClick={(e) => e.stopPropagation()} onSubmit={saveAddGuest}>
              <h2 className="cg-modal-title">{t('checkin.addGuestTitle')}</h2>
              <p className="cg-modal-sub">{t('checkin.addGuestSub')}</p>
              <label className="cg-label">{t('checkin.fName')}
                <input className="cg-input" required value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} />
              </label>
              <label className="cg-label">{t('checkin.fBirthdate')}
                <input className="cg-input" type="date" required value={addForm.birthdate} onChange={(e) => setAddForm({ ...addForm, birthdate: e.target.value })} />
              </label>
              <label className="cg-label">{t('checkin.fGender')}
                <select className="cg-input" required value={addForm.gender} onChange={(e) => setAddForm({ ...addForm, gender: e.target.value })}>
                  <option value="">{t('checkin.fSelect')}</option>
                  <option value="male">{t('checkin.fMale')}</option>
                  <option value="female">{t('checkin.fFemale')}</option>
                  <option value="other">{t('checkin.fOther')}</option>
                </select>
              </label>
              <label className="cg-label">{t('checkin.fNationality')}
                <input className="cg-input" required value={addForm.nationality} onChange={(e) => setAddForm({ ...addForm, nationality: e.target.value })} />
              </label>
              <label className="cg-label">{t('checkin.fPassportNo')}
                <input className="cg-input" required value={addForm.passport_number} onChange={(e) => setAddForm({ ...addForm, passport_number: e.target.value })} />
              </label>
              <label className="cg-label">{t('checkin.fAddress')}
                <input className="cg-input" required value={addForm.address} onChange={(e) => setAddForm({ ...addForm, address: e.target.value })} />
              </label>
              <label className="cg-label">{t('checkin.fPrev')}
                <input className="cg-input" required value={addForm.prev_accommodation} onChange={(e) => setAddForm({ ...addForm, prev_accommodation: e.target.value })} />
              </label>
              <label className="cg-label">{t('checkin.fNext')}
                <input className="cg-input" required value={addForm.next_destination} onChange={(e) => setAddForm({ ...addForm, next_destination: e.target.value })} />
              </label>
              {error && <p className="cg-error">{error}</p>}
              <p className="cg-modal-note">{t('checkin.addGuestNote')}</p>
              <div className="cg-actions">
                <button type="button" className="cg-btn cg-btn-ghost" disabled={busy} onClick={() => setAddForm(null)}>{t('checkin.back')}</button>
                <button type="submit" className="cg-btn cg-btn-primary" disabled={busy}>{busy ? t('checkin.submitting') : t('checkin.addGuestSave')}</button>
              </div>
            </form>
          </div>
        )}
      </div>
    );
  }

  // ====== 画面3: 完了（ルール案内） ======
  return (
    <div className="cg-wrap">
      <LangBar />
      <Stepper />
      <div className="cg-card cg-card-center">
        <div className="cg-done-check">✓</div>
        <h1 className="cg-title">{t('checkin.doneTitle')}</h1>
        <p className="cg-sub">{t('checkin.doneBody')}</p>
        <div className="cg-rules-box">
          <h2 className="cg-rules-head">{t('checkin.rulesHead')}</h2>
          <ul className="cg-rules-list">
            {(t('checkin.rulesList', { returnObjects: true }) as string[]).map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
        <button className="cg-btn cg-btn-ghost" onClick={() => { setStep('verify'); setData(null); setGuests([]); setFacilityCode(fixedFacility); setCheckin(urlDate); }}>
          {t('checkin.finish')}
        </button>
      </div>
    </div>
  );
}
